import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  HStack,
  Tabs,
  TabList,
  Tab,
  Spinner,
} from '@chakra-ui/react';
import { Trophy, Medal, Award } from 'lucide-react';
import { subscribeToLeaderboard, LeaderboardEntry, LeaderboardPeriod } from '../services/leaderboardService';
import { POKER_COLORS } from '../theme/designSystem';

interface LeaderboardPanelProps {
  currentUid?: string;
  maxEntries?: number;
}

const LeaderboardPanel: React.FC<LeaderboardPanelProps> = ({ 
  currentUid,
  maxEntries = 10 
}) => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [period, setPeriod] = useState<LeaderboardPeriod>('allTime');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToLeaderboard(
      (entries) => {
        setLeaderboard(entries);
        setLoading(false);
      },
      period,
      maxEntries
    );
    return () => unsubscribe();
  }, [period, maxEntries]);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return <Trophy size={16} color="#FFD700" />;
      case 2: return <Medal size={16} color="#C0C0C0" />;
      case 3: return <Award size={16} color="#CD7F32" />;
      default: return null;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return 'gray.500';
    }
  };

  const isCurrentUser = (entryUid: string) => entryUid === currentUid;

  return (
    <Box
      bg={POKER_COLORS.cardBg}
      border={`1px solid ${POKER_COLORS.cyan}44`}
      borderRadius="12px"
      p={4}
      w="100%"
    >
      <HStack justify="space-between" mb={3}>
        <HStack spacing={2}>
          <Trophy size={20} color={POKER_COLORS.cyan} />
          <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="lg">
            Leaderboard
          </Text>
        </HStack>
      </HStack>

      <Tabs
        variant="soft-rounded"
        size="sm"
        mb={4}
        index={period === 'allTime' ? 0 : period === 'weekly' ? 1 : 2}
        onChange={(index) => {
          const periods: LeaderboardPeriod[] = ['allTime', 'weekly', 'monthly'];
          setPeriod(periods[index]);
        }}
      >
        <TabList bg={`${POKER_COLORS.darkTeal}44`} borderRadius="full" p={1}>
          <Tab
            _selected={{ bg: POKER_COLORS.cyan, color: POKER_COLORS.darkTeal }}
            color="gray.400"
            fontWeight="bold"
            fontSize="xs"
          >
            All Time
          </Tab>
          <Tab
            _selected={{ bg: POKER_COLORS.cyan, color: POKER_COLORS.darkTeal }}
            color="gray.400"
            fontWeight="bold"
            fontSize="xs"
          >
            Weekly
          </Tab>
          <Tab
            _selected={{ bg: POKER_COLORS.cyan, color: POKER_COLORS.darkTeal }}
            color="gray.400"
            fontWeight="bold"
            fontSize="xs"
          >
            Monthly
          </Tab>
        </TabList>
      </Tabs>

      {loading ? (
        <VStack py={8}>
          <Spinner color={POKER_COLORS.cyan} />
        </VStack>
      ) : leaderboard.length === 0 ? (
        <VStack py={8}>
          <Text color="gray.500" fontSize="sm">No data yet</Text>
          <Text color="gray.600" fontSize="xs">Win hands to climb the ranks!</Text>
        </VStack>
      ) : (
        <VStack spacing={2} align="stretch">
          {leaderboard.map((entry) => {
            const isCurrent = isCurrentUser(entry.userId);
            return (
              <HStack
                key={entry.userId}
                p={2}
                bg={isCurrent ? `${POKER_COLORS.cyan}22` : 'transparent'}
                borderRadius="8px"
                border={isCurrent ? `1px solid ${POKER_COLORS.cyan}44` : '1px solid transparent'}
                cursor="pointer"
                _hover={{ bg: `${POKER_COLORS.cyan}11` }}
                onClick={() => navigate(`/profile/${entry.userId}`)}
              >
                <HStack w="40px" justify="center">
                  {getRankIcon(entry.rank || 0) || (
                    <Text color={getRankColor(entry.rank || 0)} fontWeight="bold" fontSize="sm">
                      #{entry.rank}
                    </Text>
                  )}
                </HStack>

                <Box
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  bg={`linear-gradient(135deg, ${POKER_COLORS.cyan} 0%, ${POKER_COLORS.darkTeal} 100%)`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text color="white" fontWeight="bold" fontSize="sm">
                    {entry.username?.charAt(0).toUpperCase() || '?'}
                  </Text>
                </Box>

                <Text
                  flex={1}
                  color={isCurrent ? POKER_COLORS.cyan : 'white'}
                  fontWeight={isCurrent ? 'bold' : 'normal'}
                  fontSize="sm"
                  isTruncated
                >
                  {entry.username}
                  {isCurrent && ' (You)'}
                </Text>

                <HStack spacing={1}>
                  <Text color="#22c55e" fontWeight="bold" fontSize="sm">
                    {entry.handsWon}
                  </Text>
                  <Text color="gray.500" fontSize="xs">
                    wins
                  </Text>
                </HStack>
              </HStack>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default LeaderboardPanel;